/**
 * sectionBlocks.tsx — Componentes web dos blocos de seção genéricos
 *
 * Espelham, no editor e na pré-visualização, o mesmo desenho que o compilador
 * HTML compartilhado gera na publicação. Sem imagens pesadas, sem JavaScript:
 * apenas estrutura e estilo, para a página abrir rápido em qualquer aparelho.
 *
 * RELEASE: v1.2.0 - 2026-09-11 - Planos, FAQ, etapas e contato adotam os
 *            cartões adaptáveis compartilhados, sem bordas claras rígidas.
 * RELEASE: v1.1.0 - 2026-09-11 - Seção em HTML livre (`section.html`)
 * RELEASE: v1.0.0 - 2026-09-10 - Versão inicial (Studio, construtor de blocos prontos)
 */

import { resolveAssetUrl } from '@/lib/studio/core/assets/stockCatalog';
import React from 'react';
import {
  renderStudioHtmlSection,
  type StudioHtmlSectionProps,
} from '@/lib/studio/html/section';
import type { StudioWebBlockComponent } from './types';

/** Quebra o conteúdo em linhas e cada linha em partes separadas por "|". */
function linhas(valor: unknown): string[][] {
  return String(valor ?? '')
    .split('\n')
    .map((linha) => linha.trim())
    .filter(Boolean)
    .map((linha) => linha.split('|').map((parte) => parte.trim()));
}

/** Grade responsiva simples com o número de colunas pedido. */
function grade(colunas: unknown, minimo = 3): React.CSSProperties {
  const quantidade = Number(colunas) || minimo;
  return { gridTemplateColumns: `repeat(${quantidade}, minmax(0, 1fr))` };
}

/** Título de seção com chapéu e subtítulo. */
export const SectionHeadingBlock: StudioWebBlockComponent = ({ node }) => {
  const alinhamento = (node.props.align as string) === 'left' ? 'left' : 'center';
  return (
    <header className="py-8" style={{ textAlign: alinhamento as React.CSSProperties['textAlign'] }}>
      {node.props.eyebrow ? (
        <p
          className="text-xs font-semibold uppercase tracking-widest"
          style={{ color: (node.props.accentColor as string) || undefined }}
        >
          {node.props.eyebrow as string}
        </p>
      ) : null}
      <h2 className="mt-2 text-2xl font-semibold tracking-tight sm:text-3xl">
        {(node.props.title as string) || ''}
      </h2>
      {node.props.subtitle ? (
        <p className="mx-auto mt-2 max-w-2xl text-sm opacity-75">{node.props.subtitle as string}</p>
      ) : null}
    </header>
  );
};

/** Texto ao lado de uma imagem. */
export const SectionSplitBlock: StudioWebBlockComponent = ({ node }) => {
  const imagemPrimeiro = (node.props.imagePosition as string) === 'left';
  const imagem = node.props.imageUrl ? (
    <img
      src={resolveAssetUrl(node.props.imageUrl as string)}
      alt={(node.props.title as string) || ''}
      className="w-full rounded-2xl object-cover"
      loading="lazy"
    />
  ) : null;

  return (
    <section
      className="grid items-center gap-8 rounded-2xl py-10 sm:grid-cols-2"
      style={{ backgroundColor: (node.props.backgroundColor as string) || undefined }}
    >
      {imagemPrimeiro ? imagem : null}
      <div>
        {node.props.eyebrow ? (
          <p className="text-xs font-semibold uppercase tracking-widest opacity-70">
            {node.props.eyebrow as string}
          </p>
        ) : null}
        <h2 className="mt-2 text-2xl font-semibold">{(node.props.title as string) || ''}</h2>
        <div
          className="prose prose-sm mt-3 max-w-none text-sm"
          dangerouslySetInnerHTML={{ __html: (node.props.text as string) || '' }}
        />
        {node.props.ctaLabel ? (
          <a
            href={(node.props.ctaHref as string) || '#'}
            className="mt-5 inline-flex h-11 items-center rounded-xl bg-primary px-6 text-sm font-semibold text-primary-foreground"
          >
            {node.props.ctaLabel as string}
          </a>
        ) : null}
      </div>
      {imagemPrimeiro ? null : imagem}
    </section>
  );
};

/** Galeria de imagens. */
export const SectionGalleryBlock: StudioWebBlockComponent = ({ node }) => {
  const itens = linhas(node.props.items);
  const proporcao =
    (node.props.ratio as string) === 'square'
      ? '1 / 1'
      : (node.props.ratio as string) === 'portrait'
        ? '3 / 4'
        : '4 / 3';
  return (
    <section className="py-8">
      {node.props.title ? (
        <h2 className="mb-6 text-center text-2xl font-semibold">{node.props.title as string}</h2>
      ) : null}
      <div className="st-grid-auto grid gap-4" style={grade(node.props.columns)}>
        {itens.map(([url, legenda], indice) => (
          <figure key={`${url}-${indice}`}>
            <img
              src={url}
              alt={legenda || ''}
              className="w-full rounded-xl object-cover"
              style={{ aspectRatio: proporcao }}
              loading="lazy"
            />
            {legenda ? (
              <figcaption className="mt-2 text-xs text-muted-foreground">{legenda}</figcaption>
            ) : null}
          </figure>
        ))}
      </div>
    </section>
  );
};

/** Vídeo incorporado (ou apenas a imagem de capa, quando não houver endereço). */
export const SectionVideoBlock: StudioWebBlockComponent = ({ node }) => (
  <section className="py-8">
    {node.props.title ? (
      <h2 className="mb-6 text-center text-2xl font-semibold">{node.props.title as string}</h2>
    ) : null}
    <div className="overflow-hidden rounded-2xl border" style={{ aspectRatio: '16 / 9' }}>
      {node.props.videoUrl ? (
        <iframe
          src={node.props.videoUrl as string}
          title={(node.props.title as string) || 'Vídeo'}
          className="h-full w-full"
          allow="accelerometer; autoplay; encrypted-media; picture-in-picture"
          allowFullScreen
        />
      ) : node.props.posterUrl ? (
        <img
          src={node.props.posterUrl as string}
          alt={(node.props.caption as string) || ''}
          className="h-full w-full object-cover"
          loading="lazy"
        />
      ) : (
        <div className="flex h-full items-center justify-center text-xs text-muted-foreground">
          Informe o endereço de incorporação do vídeo
        </div>
      )}
    </div>
    {node.props.caption ? (
      <p className="mt-2 text-center text-xs text-muted-foreground">{node.props.caption as string}</p>
    ) : null}
  </section>
);

/** Números e resultados. */
export const SectionStatsBlock: StudioWebBlockComponent = ({ node }) => {
  const itens = linhas(node.props.items);
  const fundo = (node.props.backgroundColor as string) || undefined;
  return (
    <section
      className="rounded-2xl py-8"
      style={{ backgroundColor: fundo, color: fundo ? '#ffffff' : undefined }}
    >
      {node.props.title ? (
        <h2 className="mb-6 text-center text-2xl font-semibold">{node.props.title as string}</h2>
      ) : null}
      <div className="st-grid-auto grid gap-6 px-4 text-center" style={grade(node.props.columns)}>
        {itens.map(([valor, descricao], indice) => (
          <div key={`${valor}-${indice}`}>
            <p className="text-3xl font-bold tracking-tight">{valor}</p>
            {descricao ? <p className="mt-1 text-sm opacity-75">{descricao}</p> : null}
          </div>
        ))}
      </div>
    </section>
  );
};

/** Logos de clientes e parceiros. */
export const SectionLogosBlock: StudioWebBlockComponent = ({ node }) => {
  const itens = linhas(node.props.items);
  return (
    <section className="py-8">
      {node.props.title ? (
        <p className="mb-5 text-center text-xs font-semibold uppercase tracking-widest text-muted-foreground">
          {node.props.title as string}
        </p>
      ) : null}
      <div className="flex flex-wrap items-center justify-center gap-8">
        {itens.length === 0 ? (
          <p className="text-xs text-muted-foreground">
            Informe os endereços das imagens dos logos.
          </p>
        ) : (
          itens.map(([url, nome], indice) => (
            <img
              key={`${url}-${indice}`}
              src={url}
              alt={nome || ''}
              className="h-8 w-auto object-contain"
              style={{ filter: node.props.grayscale ? 'grayscale(1)' : undefined, opacity: node.props.grayscale ? 0.7 : 1 }}
              loading="lazy"
            />
          ))
        )}
      </div>
    </section>
  );
};

/** Planos e preços. */
export const SectionPricingBlock: StudioWebBlockComponent = ({ node }) => {
  const itens = linhas(node.props.items);
  return (
    <section className="py-8">
      {node.props.title ? (
        <h2 className="mb-6 text-center text-2xl font-semibold">{node.props.title as string}</h2>
      ) : null}
      <div className="st-grid-auto grid gap-5" style={grade(node.props.columns)}>
        {itens.map(([nome, preco, periodo, recursos, destaque, botao, href], indice) => {
          const emDestaque = (destaque || '').toLowerCase() === 'sim';
          return (
            <div
              key={`${nome}-${indice}`}
              className={`page-card page-plan ${emDestaque ? 'page-plan-featured' : ''}`}
            >
              <p className="text-sm font-semibold uppercase tracking-wide opacity-70">{nome}</p>
              <p className="mt-3 text-3xl font-bold">{preco}</p>
              {periodo ? <p className="text-xs text-muted-foreground">{periodo}</p> : null}
              <ul className="mt-4 flex-1 space-y-2 text-sm">
                {(recursos || '')
                  .split(';')
                  .map((recurso) => recurso.trim())
                  .filter(Boolean)
                  .map((recurso) => (
                    <li key={recurso}>• {recurso}</li>
                  ))}
              </ul>
              {botao ? (
                <a
                  href={href || '#formulario'}
                  className="mt-5 inline-flex h-10 items-center justify-center rounded-xl bg-primary px-5 text-sm font-semibold text-primary-foreground"
                >
                  {botao}
                </a>
              ) : null}
            </div>
          );
        })}
      </div>
      {node.props.note ? (
        <p className="mt-4 text-center text-xs text-muted-foreground">{node.props.note as string}</p>
      ) : null}
    </section>
  );
};

/** Perguntas frequentes. */
export const SectionFaqBlock: StudioWebBlockComponent = ({ node }) => {
  const itens = linhas(node.props.items);
  return (
    <section className="py-8">
      {node.props.title ? (
        <h2 className="mb-6 text-center text-2xl font-semibold">{node.props.title as string}</h2>
      ) : null}
      <div className="st-grid-auto grid gap-4" style={grade(node.props.columns, 1)}>
        {itens.map(([pergunta, resposta], indice) => (
          <div key={`${pergunta}-${indice}`} className="page-card">
            <p className="text-sm font-semibold">{pergunta}</p>
            {resposta ? <p className="mt-2 text-sm text-muted-foreground">{resposta}</p> : null}
          </div>
        ))}
      </div>
    </section>
  );
};

/** Etapas / passo a passo. */
export const SectionStepsBlock: StudioWebBlockComponent = ({ node }) => {
  const itens = linhas(node.props.items);
  const vertical = (node.props.layout as string) === 'vertical';
  return (
    <section className="py-8">
      {node.props.title ? (
        <h2 className="mb-6 text-center text-2xl font-semibold">{node.props.title as string}</h2>
      ) : null}
      <div
        className="st-grid-auto grid gap-5"
        style={vertical ? undefined : grade(String(itens.length || 3))}
      >
        {itens.map(([titulo, descricao], indice) => (
          <div key={`${titulo}-${indice}`} className="page-card page-step">
            <span className="flex h-7 w-7 shrink-0 items-center justify-center rounded-full bg-primary text-xs font-bold text-primary-foreground">
              {indice + 1}
            </span>
            <div>
              <p className="text-sm font-semibold">{titulo}</p>
              {descricao ? <p className="mt-1 text-sm text-muted-foreground">{descricao}</p> : null}
            </div>
          </div>
        ))}
      </div>
    </section>
  );
};

/** Equipe. */
export const SectionTeamBlock: StudioWebBlockComponent = ({ node }) => {
  const itens = linhas(node.props.items);
  return (
    <section className="py-8">
      {node.props.title ? (
        <h2 className="mb-6 text-center text-2xl font-semibold">{node.props.title as string}</h2>
      ) : null}
      <div className="st-grid-auto grid gap-5 text-center" style={grade(node.props.columns, 4)}>
        {itens.map(([foto, nome, cargo], indice) => (
          <div key={`${nome}-${indice}`}>
            {foto ? (
              <img
                src={foto}
                alt={nome || ''}
                className="mx-auto h-24 w-24 rounded-full object-cover"
                loading="lazy"
              />
            ) : (
              <div className="mx-auto h-24 w-24 rounded-full bg-muted" />
            )}
            <p className="mt-3 text-sm font-semibold">{nome}</p>
            {cargo ? <p className="text-xs text-muted-foreground">{cargo}</p> : null}
          </div>
        ))}
      </div>
    </section>
  );
};

/** Chamada para ação. */
export const SectionCtaBlock: StudioWebBlockComponent = ({ node }) => {
  const alinhamento = (node.props.align as string) === 'left' ? 'left' : 'center';
  return (
    <section
      className="rounded-2xl px-6 py-10 sm:px-10"
      style={{
        backgroundColor: (node.props.backgroundColor as string) || undefined,
        color: (node.props.textColor as string) || undefined,
        textAlign: alinhamento as React.CSSProperties['textAlign'],
      }}
    >
      <h2 className="text-2xl font-semibold">{(node.props.title as string) || ''}</h2>
      {node.props.subtitle ? (
        <p className="mt-2 text-sm opacity-80">{node.props.subtitle as string}</p>
      ) : null}
      {node.props.buttonLabel ? (
        <a
          href={(node.props.buttonHref as string) || '#formulario'}
          className="mt-6 inline-flex h-11 items-center rounded-xl bg-primary px-6 text-sm font-semibold text-primary-foreground"
        >
          {node.props.buttonLabel as string}
        </a>
      ) : null}
    </section>
  );
};

/** Prazo / validade da oferta. */
export const SectionDeadlineBlock: StudioWebBlockComponent = ({ node }) => (
  <section
    className="rounded-xl px-5 py-4 text-center"
    style={{
      backgroundColor: (node.props.backgroundColor as string) || undefined,
      color: (node.props.textColor as string) || undefined,
    }}
  >
    <p className="text-sm font-semibold">{(node.props.title as string) || ''}</p>
    {node.props.note ? <p className="mt-1 text-xs opacity-80">{node.props.note as string}</p> : null}
  </section>
);

/** Informações de contato. */
export const SectionContactBlock: StudioWebBlockComponent = ({ node }) => {
  const itens = linhas(node.props.items);
  return (
    <section className="py-8">
      {node.props.title ? (
        <h2 className="mb-6 text-center text-2xl font-semibold">{node.props.title as string}</h2>
      ) : null}
      <div className="st-grid-auto grid gap-4 text-center" style={grade(node.props.columns)}>
        {itens.map(([rotulo, valor], indice) => (
          <div key={`${rotulo}-${indice}`} className="page-card">
            <p className="text-xs font-semibold uppercase tracking-wide text-muted-foreground">
              {rotulo}
            </p>
            <p className="mt-1 text-sm font-medium">{valor}</p>
          </div>
        ))}
      </div>
    </section>
  );
};

/** Menu do topo. */
export const SectionNavBlock: StudioWebBlockComponent = ({ node }) => {
  const itens = linhas(node.props.links);
  const fundo = (node.props.backgroundColor as string) || undefined;
  return (
    <nav
      className="flex flex-wrap items-center justify-between gap-4 rounded-xl px-5 py-3"
      style={{
        backgroundColor: fundo,
        color: (node.props.textColor as string) || undefined,
      }}
    >
      <div className="flex items-center gap-2">
        {node.props.logoUrl ? (
          <img src={node.props.logoUrl as string} alt="" className="h-7 w-auto" loading="lazy" />
        ) : null}
        <span className="text-sm font-semibold">{(node.props.brand as string) || ''}</span>
      </div>
      <div className="flex flex-wrap items-center gap-5 text-sm">
        {itens.map(([rotulo, destino], indice) => (
          <a key={`${rotulo}-${indice}`} href={destino || '#'} className="opacity-80 hover:opacity-100">
            {rotulo}
          </a>
        ))}
        {node.props.ctaLabel ? (
          <a
            href={(node.props.ctaHref as string) || '#formulario'}
            className="inline-flex h-9 items-center rounded-lg bg-primary px-4 text-xs font-semibold text-primary-foreground"
          >
            {node.props.ctaLabel as string}
          </a>
        ) : null}
      </div>
    </nav>
  );
};

/** Rodapé. */
export const SectionFooterBlock: StudioWebBlockComponent = ({ node }) => {
  const itens = linhas(node.props.links);
  return (
    <footer
      className="rounded-2xl px-6 py-8"
      style={{
        backgroundColor: (node.props.backgroundColor as string) || undefined,
        color: (node.props.textColor as string) || undefined,
      }}
    >
      <p className="text-sm font-semibold">{(node.props.brand as string) || ''}</p>
      {node.props.text ? <p className="mt-1 text-sm opacity-75">{node.props.text as string}</p> : null}
      {itens.length > 0 ? (
        <div className="mt-4 flex flex-wrap gap-4 text-sm">
          {itens.map(([rotulo, destino], indice) => (
            <a key={`${rotulo}-${indice}`} href={destino || '#'} className="opacity-80 hover:opacity-100">
              {rotulo}
            </a>
          ))}
        </div>
      ) : null}
      {node.props.legal ? (
        <p className="mt-5 text-xs opacity-60">{node.props.legal as string}</p>
      ) : null}
    </footer>
  );
};

/** Faixa de aviso. */
export const SectionBannerBlock: StudioWebBlockComponent = ({ node }) => (
  <div
    className="flex flex-wrap items-center justify-center gap-3 rounded-lg px-4 py-2 text-center text-xs"
    style={{
      backgroundColor: (node.props.backgroundColor as string) || undefined,
      color: (node.props.textColor as string) || undefined,
    }}
  >
    <span>{(node.props.text as string) || ''}</span>
    {node.props.linkLabel ? (
      <a href={(node.props.linkHref as string) || '#'} className="font-semibold underline">
        {node.props.linkLabel as string}
      </a>
    ) : null}
  </div>
);

// =============================================================================
// Seção em HTML livre (páginas criadas por IA e importadas como blocos)
// =============================================================================

/**
 * Desenha uma seção de HTML livre usando EXATAMENTE o mesmo montador da página
 * publicada e do PDF. O conteúdo já foi saneado no servidor e volta a ser
 * saneado na publicação; aqui é só a prévia dentro do editor.
 */
export const RawHtmlSectionBlock: StudioWebBlockComponent = ({ node }) => (
  <div
    dangerouslySetInnerHTML={{
      __html: renderStudioHtmlSection(node.props as StudioHtmlSectionProps, 'editor'),
    }}
  />
);
